import "./layout.css";

import React, { useEffect, useMemo, useState } from "react";

import { Badge, Layout, Menu } from "antd";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { signOut } from "firebase/auth";

import {
  CalendarOutlined,
  DashboardOutlined,
  FileProtectOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  MailOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { auth } from "../firebase";
import { userData, setUnreadThreadIds } from "../redux/userDataSlice";
import { subscribeUnreadThreadIds } from "../helper/discussions";

import { homeIcon, productIcon, serviceIcon } from "../assets";
import brandMark from "../assets/icon.png";
import Header2 from "../components/header/header";
import Loader from "../components/loader/loader";
import { GreenNotify, useNotificationListener } from "../helper/helper";
import { useTaskAttentionCount } from "../helper/propertyTasks";
import LandingPage from "../pages/landingPage/landingPage";
import Privacy from "../pages/privacyPolicies/privacyPolicies";
import FAQS from "../pages/faqsList/faqsList";
import TermsOfUse from "../pages/termsOfUse/termsOfUse";
import PromotorList from "../pages/promoterList/promoterList";
import CompetitorList from "../pages/promoterList/competiterList";
import TaskDetails from "../pages/tasks/taskDetails";
import PropertyMap from "../pages/propertyMap/propertyMap";
import Checklists from "../pages/checklists/checklists";
import Notifications from "../pages/notifications/notifications";
import EmailReports from "../pages/emailReports/emailReports";
import PropertyTasks from "../pages/propertyTasks/propertyTasks";
import Vendors from "../pages/vendors/vendors";
import InspectionsScheduler from "../pages/inspectionsScheduler/inspectionsScheduler";
import Messages from "../pages/messages/messages";

const { Content, Sider } = Layout;

const DASHBOARD_ROUTES = [
  { path: "/", element: <LandingPage /> },
  { path: "/dashboard", element: <LandingPage /> },
  { path: "/users", element: <PromotorList /> },
  { path: "/properties", element: <CompetitorList /> },
  { path: "/properties/:id", element: <TaskDetails /> },
  { path: "/property-map", element: <PropertyMap /> },
  { path: "/inspections", element: <InspectionsScheduler /> },
  { path: "/checklists", element: <Checklists /> },
  { path: "/tasks", element: <PropertyTasks /> },
  { path: "/vendors", element: <Vendors /> },
  { path: "/messages", element: <Messages /> },
  { path: "/email-reports", element: <EmailReports /> },
  { path: "/notifications", element: <Notifications /> },
  { path: "/privacy-policy", element: <Privacy /> },
  { path: "/terms-of-use", element: <TermsOfUse /> },
  { path: "/about-us", element: <FAQS /> },
];

// Sidebar nav grouped into labelled sections. The first section has no label
// (Dashboard sits on its own at the top). SIDEBAR_MENU_ITEMS below flattens
// these for the active-key / section-title lookups.
const NAV_SECTIONS = [
  {
    label: null,
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        path: "/dashboard",
        icon: <DashboardOutlined className="side-bar-icon" />,
      },
    ],
  },
  {
    label: "Properties",
    items: [
      {
        key: "properties",
        label: "Properties",
        path: "/properties",
        icon: serviceIcon,
      },
      {
        key: "property-map",
        label: "Property Map",
        path: "/property-map",
        icon: homeIcon,
      },
      {
        key: "inspections",
        label: "Inspections Scheduler",
        path: "/inspections",
        icon: <CalendarOutlined className="side-bar-icon" />,
      },
    ],
  },
  {
    label: "Work",
    items: [
      { key: "tasks", label: "Tasks", path: "/tasks", icon: serviceIcon },
      {
        key: "checklists",
        label: "Checklists",
        path: "/checklists",
        icon: productIcon,
      },
      {
        key: "vendors",
        label: "Vendors",
        path: "/vendors",
        icon: <TeamOutlined className="side-bar-icon" />,
      },
    ],
  },
  {
    label: "People",
    items: [
      {
        key: "users",
        label: "Users",
        path: "/users",
        icon: <TeamOutlined className="side-bar-icon" />,
      },
      {
        key: "messages",
        label: "Messages",
        path: "/messages",
        icon: <MessageOutlined className="side-bar-icon" />,
      },
    ],
  },
  {
    label: "Reports & Legal",
    items: [
      {
        key: "email-reports",
        label: "Email Reports",
        path: "/email-reports",
        icon: <MailOutlined className="side-bar-icon" />,
      },
      {
        key: "privacy-policy",
        label: "Privacy Policy",
        path: "/privacy-policy",
        icon: <SafetyCertificateOutlined className="side-bar-icon" />,
      },
      {
        key: "terms-of-use",
        label: "Terms Of Use",
        path: "/terms-of-use",
        icon: <FileProtectOutlined className="side-bar-icon" />,
      },
      {
        key: "about-us",
        label: "About Us",
        path: "/about-us",
        icon: <InfoCircleOutlined className="side-bar-icon" />,
      },
    ],
  },
];

const SIDEBAR_MENU_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

const LayoutDashboard = () => {
  const [isloading, setIsLoading] = useState(false);
  // Drawer mode (tablet/mobile): sidebar slides in over an overlay.
  const [isCompact, setIsCompact] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Live notifications target the logged-in admin (read from redux the same way
  // the header/landing page do); replaces a previously hardcoded uid.
  const adminUser = useSelector((state) => state.userDataSlice?.userData);
  const adminUid = adminUser?.uuid || adminUser?._id;

  // Switch to drawer navigation at/below 1024px (tablet + mobile).
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => {
      setIsCompact(mq.matches);
      // Leaving compact mode: ensure the drawer state can't linger "open" and
      // resurface (already-open) the next time the viewport narrows.
      if (!mq.matches) setNavOpen(false);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Close the drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const logOut = async () => {
    try {
      // End the Firebase session (persisted in IndexedDB) — without this the
      // user stays authenticated to Firestore after "logging out".
      await signOut(auth);
    } catch (error) {
      console.warn("signOut failed:", error?.message);
    }
    // Clear the live redux store too; localStorage.clear() only wipes the
    // persisted copy, leaving in-memory userData truthy until a reload. Also
    // reset the unread set so the next admin on this browser doesn't briefly see
    // the previous admin's dots/badge (the layout listener's clear branch can't
    // run after this component unmounts on navigate).
    dispatch(userData(null));
    dispatch(setUnreadThreadIds([]));
    localStorage.clear();
    GreenNotify("Logout successful");
    navigate("/login", { replace: true });
  };
  useNotificationListener(adminUid);
  // One session-wide listener for my unread discussion-thread ids → redux. The
  // header badge and the Tasks/Inspections row dots read it from there, so they
  // don't each open their own my-threads + reads listeners.
  useEffect(() => {
    if (!adminUid) {
      dispatch(setUnreadThreadIds([]));
      return undefined;
    }
    return subscribeUnreadThreadIds(adminUid, (ids) =>
      dispatch(setUnreadThreadIds(ids)),
    );
  }, [adminUid, dispatch]);
  // Live count of tasks needing attention (open question or unassigned) —
  // drives the red badge on the Tasks nav item so it's visible from any screen.
  // Gated on adminUid so the listener waits for auth (see hook comment).
  const taskAttentionCount = useTaskAttentionCount(adminUid);
  useEffect(() => {
    return () => setIsLoading(false);
  }, []);

  const currentMenuKey = useMemo(() => {
    const activeItem = SIDEBAR_MENU_ITEMS.find((item) =>
      location.pathname.startsWith(item.path),
    );
    return activeItem?.key ?? "dashboard";
  }, [location.pathname]);

  const sectionTitle = useMemo(() => {
    const isHome =
      location.pathname === "/" || location.pathname.startsWith("/dashboard");
    if (isHome) return "Dashboard";
    const activeItem = SIDEBAR_MENU_ITEMS.find((item) =>
      location.pathname.startsWith(item.path),
    );
    return activeItem?.label ?? "Overview";
  }, [location.pathname]);

  return (
    <Layout className="dashboard-shell">
      <Loader loading={isloading} />
      {isCompact && navOpen && (
        <div
          className="dashboard-overlay"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}
      <Sider
        className={`dashboard-sider${navOpen ? " is-open" : ""}`}
        width={280}
      >
        <div className="dashboard-sider-header">
          <button
            type="button"
            className="dashboard-brand"
            onClick={() => navigate("/dashboard")}
          >
            <img className="dashboard-brand-mark" src={brandMark} alt="" />
            <span className="dashboard-brand-text">
              <span className="dashboard-brand-name">2nd Home</span>
              <span className="dashboard-brand-tag">Property Inspection</span>
            </span>
          </button>
        </div>
        <Menu
          className="dashboard-menu"
          theme="dark"
          selectedKeys={[currentMenuKey]}
          mode="inline"
        >
          <div className="dashboard-menu-scroll">
            {NAV_SECTIONS.flatMap((section, idx) => [
              section.label ? (
                <p className="dashboard-menu-label" key={`label-${idx}`}>
                  {section.label}
                </p>
              ) : null,
              ...section.items.map((item) => (
                <Menu.Item
                  key={item.key}
                  className={
                    item.key === currentMenuKey
                      ? "menu-item active-menu-item"
                      : "menu-item"
                  }
                  onClick={() => navigate(item.path)}
                  icon={
                    typeof item.icon === "string" ? (
                      <img className="side-bar-icon" src={item.icon} alt="" />
                    ) : (
                      item.icon
                    )
                  }
                >
                  {item.key === "tasks" && taskAttentionCount > 0 ? (
                    <span className="dashboard-menu-item-badge">
                      {item.label}
                      <Badge
                        count={taskAttentionCount}
                        overflowCount={99}
                        size="small"
                      />
                    </span>
                  ) : (
                    item.label
                  )}
                </Menu.Item>
              )),
            ])}
          </div>
          <Menu.Item
            onClick={logOut}
            key="logout"
            className="menu-item dashboard-logout"
            icon={<LogoutOutlined className="side-bar-icon" />}
          >
            Log Out
          </Menu.Item>
        </Menu>
      </Sider>
      <Layout className="site-layout dashboard-main">
        <Header2
          title={sectionTitle}
          onBell={() => navigate("/notifications")}
          onMessages={() => navigate("/messages")}
          showMenu={isCompact}
          onMenu={() => setNavOpen((open) => !open)}
          onLogout={logOut}
        />
        <Content className="dashboard-content">
          <Routes>
            {DASHBOARD_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

export default LayoutDashboard;
